package dairy.mynotes.login

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import dairy.mynotes.navigation.Navigator
import dairy.mynotes.reusablecomponents.ButtonComponent
import dairy.mynotes.reusablecomponents.CircularProgressBarComponent
import dairy.mynotes.reusablecomponents.ImageIcon
import dairy.mynotes.reusablecomponents.Label
import dairy.mynotes.reusablecomponents.RoundedBox
import dairy.mynotes.reusablecomponents.TextButtonComponent
import dairy.mynotes.reusablecomponents.TextInputComponent
import dairy.mynotes.ui.theme.AppColors

@Composable
fun LoginScreen(
    navigator: Navigator? = null,
    viewModel: LoginViewModel = androidx.lifecycle.viewmodel.compose.viewModel { LoginViewModel() }
) {
    val uiState by viewModel.uiState.collectAsState()
    var showForgotPasswordDialog by remember { mutableStateOf(false) }
    val keyboardController = LocalSoftwareKeyboardController.current

    // Navigate to NotesList clearing back stack so back button exits the app
    LaunchedEffect(uiState.isLoggedIn) {
        if (uiState.isLoggedIn) {
            navigator?.navigateToNotesList(clearBackStack = true)
        }
    }

    // Use BoxWithConstraints to detect landscape (width > height)
    BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
        val isLandscape = maxWidth > maxHeight
        val headerHeight = if (isLandscape) 100.dp else 220.dp
        val iconSize = if (isLandscape) 40.dp else 64.dp

        Column(
            modifier = Modifier
                .fillMaxSize()
                .background(AppColors.Background),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // ── Fixed header ────────────────────────────────────────────────────
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(headerHeight)
                    .background(AppColors.Primary),
                contentAlignment = Alignment.Center
            ) {
                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                    if (!isLandscape) {
                        RoundedBox(size = iconSize) {
                            ImageIcon(modifier = Modifier.size(iconSize))
                        }
                        Spacer(modifier = Modifier.height(10.dp))
                    }
                    Text(
                        text = "My Notes",
                        fontSize = if (isLandscape) 18.sp else 22.sp,
                        fontWeight = FontWeight.Bold,
                        color = AppColors.OnPrimary
                    )
                    if (!isLandscape) {
                        Text(
                            text = "Your personal space to think",
                            fontSize = 12.sp,
                            color = AppColors.OnPrimary.copy(alpha = 0.8f)
                        )
                    }
                }
            }

            // ── Scrollable form ──────────────────────────────────────────────────
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .fillMaxHeight()
                    .verticalScroll(rememberScrollState())
                    .padding(horizontal = 24.dp)
                    .padding(top = 24.dp, bottom = 48.dp)
            ) {
            Text(
                text = "Sign In",
                fontSize = 20.sp,
                fontWeight = FontWeight.Bold,
                color = AppColors.TextPrimary,
                modifier = Modifier.padding(bottom = 16.dp)
            )

            TextInputComponent(
                label = "Email",
                value = uiState.email,
                keyboardType = KeyboardType.Email,
                showClearIcon = true,
                onValueChange = { viewModel.onEmailChange(it) }
            )
            Spacer(modifier = Modifier.height(12.dp))
            TextInputComponent(
                label = "Password",
                value = uiState.password,
                showClearIcon = true,
                keyboardType = KeyboardType.Password,
                visualTransformation = PasswordVisualTransformation(),
                onValueChange = { viewModel.onPasswordChange(it) }
            )
            TextButtonComponent(
                text = "Forgot password?",
                modifier = Modifier.align(Alignment.End),
                onClick = {
                    viewModel.resetForgotPasswordState()
                    showForgotPasswordDialog = true
                }
            )
            Spacer(modifier = Modifier.height(8.dp))

            if (uiState.isLoading) {
                CircularProgressBarComponent(modifier = Modifier.align(Alignment.CenterHorizontally))
                Spacer(modifier = Modifier.height(8.dp))
            }
            if (uiState.error != null) {
                Text(
                    text = uiState.error ?: "",
                    color = AppColors.Error,
                    style = MaterialTheme.typography.bodySmall,
                    modifier = Modifier.padding(bottom = 8.dp)
                )
            }

            ButtonComponent(
                text = "Login",
                modifier = Modifier
                    .fillMaxWidth()
                    .height(54.dp),
                colors = androidx.compose.material3.ButtonDefaults.buttonColors(
                    containerColor = AppColors.Primary,
                    contentColor = AppColors.OnPrimary
                )
            ) {
                keyboardController?.hide()
                viewModel.login()
            }

            Spacer(modifier = Modifier.height(16.dp))

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.Center,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Label(text = "No account?", textStyle = MaterialTheme.typography.bodyMedium)
                TextButtonComponent(
                    text = "Sign up",
                    onClick = { navigator?.navigateToRegistration() }
                )
            }
        }
    }
    } // end BoxWithConstraints

    // ── Forgot Password Dialog ────────────────────────────────────────────────
    if (showForgotPasswordDialog) {
        AlertDialog(
            onDismissRequest = {
                showForgotPasswordDialog = false
                viewModel.resetForgotPasswordState()
            },
            containerColor = AppColors.Surface,
            title = {
                Text(
                    text = "Reset Password",
                    fontWeight = FontWeight.Bold,
                    color = AppColors.TextPrimary
                )
            },
            text = {
                Column {
                    if (uiState.forgotPasswordSuccess) {
                        Text(
                            text = "✅ A password reset link has been sent to:",
                            color = AppColors.TextSecondary,
                            style = MaterialTheme.typography.bodyMedium
                        )
                        Spacer(modifier = Modifier.height(8.dp))
                        Text(
                            text = uiState.forgotPasswordEmail,
                            fontWeight = FontWeight.Bold,
                            color = AppColors.Primary,
                            style = MaterialTheme.typography.bodyMedium
                        )
                        Spacer(modifier = Modifier.height(8.dp))
                        Text(
                            text = "Check your inbox and follow the link to set a new password.",
                            color = AppColors.TextSecondary,
                            style = MaterialTheme.typography.bodySmall
                        )
                    } else {
                        Text(
                            text = "Enter your registered email and we'll send a reset link.",
                            color = AppColors.TextSecondary,
                            style = MaterialTheme.typography.bodySmall
                        )
                        Spacer(modifier = Modifier.height(12.dp))
                        OutlinedTextField(
                            value = uiState.forgotPasswordEmail,
                            onValueChange = { viewModel.onForgotPasswordEmailChange(it) },
                            label = { Text("Email") },
                            singleLine = true,
                            colors = OutlinedTextFieldDefaults.colors(
                                focusedBorderColor = AppColors.Primary,
                                unfocusedBorderColor = AppColors.LightGray,
                                focusedLabelColor = AppColors.Primary
                            ),
                            modifier = Modifier.fillMaxWidth()
                        )
                        if (uiState.forgotPasswordError != null) {
                            Spacer(modifier = Modifier.height(6.dp))
                            Text(
                                text = uiState.forgotPasswordError ?: "",
                                color = AppColors.Error,
                                style = MaterialTheme.typography.bodySmall
                            )
                        }
                        if (uiState.forgotPasswordLoading) {
                            Spacer(modifier = Modifier.height(8.dp))
                            CircularProgressBarComponent(
                                modifier = Modifier.align(Alignment.CenterHorizontally)
                            )
                        }
                    }
                }
            },
            confirmButton = {
                if (uiState.forgotPasswordSuccess) {
                    TextButton(onClick = {
                        showForgotPasswordDialog = false
                        viewModel.resetForgotPasswordState()
                    }) {
                        Text("Done", color = AppColors.Primary, fontWeight = FontWeight.Bold)
                    }
                } else {
                    TextButton(
                        onClick = { viewModel.sendPasswordReset() },
                        enabled = !uiState.forgotPasswordLoading
                    ) {
                        Text("Send Link", color = AppColors.Primary, fontWeight = FontWeight.Bold)
                    }
                }
            },
            dismissButton = {
                if (!uiState.forgotPasswordSuccess) {
                    TextButton(onClick = {
                        showForgotPasswordDialog = false
                        viewModel.resetForgotPasswordState()
                    }) {
                        Text("Cancel", color = AppColors.TextSecondary)
                    }
                }
            }
        )
    }
}

@Composable
fun LoginFooter(onSignUpClick: () -> Unit = {}) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 8.dp),
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Label(text = "No account?", textStyle = MaterialTheme.typography.titleMedium)
        Spacer(modifier = Modifier.size(4.dp))
        TextButtonComponent(text = "Sign up", onClick = { onSignUpClick() })
    }
}

@Composable
fun LoginScreenPreview() {
    LoginScreen()
}
